'use client';

import { DestinationCGType, Maybe, PoiCGType, ReviewResponseCGType } from '@/types/graphql/root';
import { useEffect, useCallback, useState, useMemo } from 'react';
import PoiDetailImages from '../poi-detail/poi-detail-images';
import PoiDetailInfoContainer from '../poi-detail/poi-detail-info-container';
import { ITINERARY_HEIGHT_MODE, ItineraryHeightModeType } from '@/app/ui/trip/client-trip-page';
import { getPoiDetail } from '@/utils/actions';
import { filterNullUndefined } from '@/utils';

interface PoiModalInTripProps {
  itineraryHeightValues: {
    full: string;
    default: string;
    minimalize: string;
  };
  useItineraryHeightMode: [ItineraryHeightModeType, React.Dispatch<React.SetStateAction<ItineraryHeightModeType>>];
  selectedPoiId: string | null;
  poiModalInTripProps?: {
    isEditRole: boolean;
    iamEdit: boolean;
    editUser: string | null;
    tripId: string | null;
    tripDestinations: DestinationCGType[] | null;
    setTripDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
    scrollListToDestOnTop: (destId: string, dayNumber: number, isSmooth?: boolean, initStickyHeight?: number) => void;
  };
  isEmbedded: boolean;
}

export default function PoiModalInTrip({
  itineraryHeightValues,
  useItineraryHeightMode: [itineraryHeightMode, setItineraryHeightMode],
  selectedPoiId,
  poiModalInTripProps,
  isEmbedded,
}: PoiModalInTripProps) {
  const [poiDetail, setPoiDetail] = useState<{
    poi: Maybe<PoiCGType> | undefined;
    reviews: Maybe<ReviewResponseCGType> | undefined;
  } | null>(null);

  const handleBodyScroll = useCallback(() => {
    if (!selectedPoiId) {
      return;
    }

    const isNotHidden = document.body.style.overflowY !== 'hidden';
    if (isNotHidden) {
      document.body.style.overflowY = 'hidden';
    }

    return () => {
      if (isNotHidden) document.body.style.overflowY = 'auto';
    };
  }, [selectedPoiId]);

  useEffect(() => {
    const cleanup = handleBodyScroll();
    return cleanup;
  }, [handleBodyScroll]);

  useEffect(() => {
    if (selectedPoiId) {
      setPoiDetail(null);
      getPoiDetail(selectedPoiId as string).then((res) => {
        if (res.data?.poi?.id) {
          setPoiDetail({
            poi: res.data?.poi,
            reviews: res.data?.reviews,
          });
        }
      });
    } else {
      const $poiDetailModal = document.getElementById('poi-detail-modal');
      if ($poiDetailModal) {
        $poiDetailModal.style.height = itineraryHeightValues.default;
        setItineraryHeightMode(ITINERARY_HEIGHT_MODE.DEFAULT);
      }
      setPoiDetail(null);
    }
  }, [selectedPoiId]);

  const setupModalResizeHandler = useCallback(() => {
    const $poiDetailModal = document.getElementById('poi-detail-modal');
    const $handler = document.getElementById('poiDetailHandler');
    const $handlerHandle = document.querySelector('#poiDetailHandler span');

    if (!$handler) return;

    let isMove = false;
    let startY = 0;
    let startHeight = $poiDetailModal?.clientHeight ?? 0;
    let startMode: ItineraryHeightModeType = ITINERARY_HEIGHT_MODE.DEFAULT;

    const handlerTouchStart = (e: TouchEvent) => {
      if (
        $poiDetailModal?.style.height !== itineraryHeightValues.full &&
        $poiDetailModal?.style.height !== itineraryHeightValues.default &&
        $poiDetailModal?.style.height !== itineraryHeightValues.minimalize
      ) {
        return;
      }
      if ($poiDetailModal.style.height === itineraryHeightValues.full) startMode = ITINERARY_HEIGHT_MODE.FULL;
      else if ($poiDetailModal.style.height === itineraryHeightValues.minimalize)
        startMode = ITINERARY_HEIGHT_MODE.MINIMALIZE;
      else startMode = ITINERARY_HEIGHT_MODE.DEFAULT;

      isMove = true;
      startY = e.touches[0].clientY;
      startHeight = $poiDetailModal?.clientHeight ?? 0;
      $poiDetailModal?.style.setProperty('transition', 'none');
    };

    const handlerMouseStart = (e: MouseEvent) => {
      if (e.target !== $handler && e.target !== $handlerHandle) return;
      if (
        $poiDetailModal?.style.height !== itineraryHeightValues.full &&
        $poiDetailModal?.style.height !== itineraryHeightValues.default &&
        $poiDetailModal?.style.height !== itineraryHeightValues.minimalize
      ) {
        return;
      }
      if ($poiDetailModal.style.height === itineraryHeightValues.full) startMode = ITINERARY_HEIGHT_MODE.FULL;
      else if ($poiDetailModal.style.height === itineraryHeightValues.minimalize)
        startMode = ITINERARY_HEIGHT_MODE.MINIMALIZE;
      else startMode = ITINERARY_HEIGHT_MODE.DEFAULT;

      isMove = true;
      startY = e.clientY;
      startHeight = $poiDetailModal?.clientHeight ?? 0;
      $poiDetailModal?.style.setProperty('transition', 'none');
    };

    const handlerTouchMove = (e: TouchEvent) => {
      if (!isMove) return;
      const currentY = e.touches[0].clientY;
      const currentHeight = startHeight + (startY - currentY);
      $poiDetailModal?.style.setProperty('height', `${currentHeight}px`);
    };

    const handlerMouseMove = (e: MouseEvent) => {
      if (!isMove) return;
      const currentY = e.clientY;
      const currentHeight = startHeight + (startY - currentY);
      $poiDetailModal?.style.setProperty('height', `${currentHeight}px`);
    };

    const handlerEnd = (e: TouchEvent | MouseEvent) => {
      if (!isMove) return;
      isMove = false;
      const endHeight = $poiDetailModal?.clientHeight ?? 0;

      let direction: 'up' | 'down' | null = null;
      if (endHeight > startHeight) direction = 'up';
      else if (endHeight < startHeight) direction = 'down';

      if (!direction) return;

      $poiDetailModal?.style.setProperty('transition', 'height 150ms ease-in-out');
      if (startMode === ITINERARY_HEIGHT_MODE.FULL) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.FULL);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.full}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.DEFAULT);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.default}`);
        }
      } else if (startMode === ITINERARY_HEIGHT_MODE.DEFAULT) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.FULL);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.full}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.MINIMALIZE);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.minimalize}`);
        }
      } else if (startMode === ITINERARY_HEIGHT_MODE.MINIMALIZE) {
        if (direction === 'up') {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.DEFAULT);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.default}`);
        } else {
          setItineraryHeightMode(ITINERARY_HEIGHT_MODE.MINIMALIZE);
          $poiDetailModal?.style.setProperty('height', `${itineraryHeightValues.minimalize}`);
        }
      }
    };

    $handler.addEventListener('touchstart', handlerTouchStart);
    document.addEventListener('mousedown', handlerMouseStart);
    $handler.addEventListener('touchmove', handlerTouchMove);
    document.addEventListener('mousemove', handlerMouseMove);
    $handler.addEventListener('touchend', handlerEnd);
    document.addEventListener('mouseup', handlerEnd);

    return () => {
      $handler.removeEventListener('touchstart', handlerTouchStart);
      document.removeEventListener('mousedown', handlerMouseStart);
      $handler.removeEventListener('touchmove', handlerTouchMove);
      document.removeEventListener('mousemove', handlerMouseMove);
      $handler.removeEventListener('touchend', handlerEnd);
      document.removeEventListener('mouseup', handlerEnd);
    };
  }, [itineraryHeightValues, setItineraryHeightMode, selectedPoiId]);

  useEffect(() => {
    const cleanup = setupModalResizeHandler();
    return cleanup;
  }, [setupModalResizeHandler]);

  const filteredPoiImages = useMemo(() => {
    if (!selectedPoiId || !poiDetail?.poi?.images) return [];
    return filterNullUndefined(poiDetail?.poi?.images?.map((image) => image?.imageUrl) ?? []);
  }, [selectedPoiId, poiDetail?.poi?.images]);

  if (!selectedPoiId) return null;

  return (
    <div
      id='poi-detail-modal'
      className='fixed bottom-0 w-full max-w-480pxr overflow-hidden rounded-t-16pxr flex flex-col z-10 bg-white poi-modal-in-main'
      style={{
        height: itineraryHeightValues?.[itineraryHeightMode.toLowerCase() as keyof typeof itineraryHeightValues],
      }}
    >
      <button id='poiDetailHandler' className='flex justify-center w-full bg-white pt-12pxr pb-32pxr'>
        <span className='w-45pxr h-4pxr rounded-full bg-[#D9D9D9] block'></span>
      </button>

      <div className={`poi-modal w-full max-w-480pxr h-dvh overflow-y-scroll bg-white`}>
        <PoiDetailImages
          poiImages={filteredPoiImages}
          poiId={poiDetail?.poi?.id ?? null}
          poiName={poiDetail?.poi?.name ?? null}
          poiType={poiDetail?.poi?.poiType ?? null}
          showFallback={true}
        />
        <PoiDetailInfoContainer
          poi={poiDetail?.poi ?? null}
          reviews={poiDetail?.reviews ?? null}
          hasAddScheduleButton={false}
          poiModalInTripProps={poiModalInTripProps}
          isEmbedded={isEmbedded}
        />
        <div
          style={{
            height: `calc(
              ${itineraryHeightValues?.[itineraryHeightMode.toLowerCase() as keyof typeof itineraryHeightValues]} - 142px)`,
          }}
        ></div>
      </div>
    </div>
  );
}
